import type { AllocationRiskProfile } from '../../../../expert-review/types/allocation.types'
import { useState } from 'react'
import { useBottomSheet } from '../../../../../../state-management/contexts/BottomSheetContext'
import { EventNames } from '../../../../../../types/misc.types'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../../utils/appBridge'
import { convertEpochToDate } from '../../../../../../utils/utils'
import { useWebViewPageVisibility } from '../../../../../../hooks/useWebViewPageVisibility'
import { PlanDetailsBottomSheet } from '../../../plans-ui-components/PlanDetailsBottomSheet'
import {
  PlansLumpsumListEventIds,
  PlansLumpsumListScreenByPlanMode
} from '../../../analytics/plansEvents'
import { useLumpsumPlansContext } from '../LumpsumPlansProvider'
import { PlansPlanMode } from '../../api/types'
import { LUMPSUM_IDEAL_PLANS_FAQS } from './lumpsumIdealPlansFaqs'
import { LUMPSUM_PLANS_FAQS } from './lumpsumPlansFaqs'
import { LUMPSUM_REBALANCE_PLANS_FAQS } from './lumpsumRebalancePlansFaqs'
import { PlansFaqsSection, type PlansFaq } from './PlansFaqsSection'
import { ResetStrategyBanner } from './ResetStrategyBanner'
import { ResetStrategyBottomSheet } from './ResetStrategyBottomSheet'

const FAQ_PREVIEW_COUNT = 3

export function LumpsumPlansFooter() {
  const {
    planMode,
    idealData,
    rebalancePlansData,
    allocationPlansData,
    allocationPlansQuery,
    idealPlansQuery,
    markTimerResumeRefetch
  } = useLumpsumPlansContext()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const [awaitingResume, setAwaitingResume] = useState(false)

  const listPlansData =
    planMode === PlansPlanMode.REBALANCE ? rebalancePlansData : allocationPlansData
  const isIdealPortfolioTab = planMode === PlansPlanMode.IDEAL_PORTFOLIO
  const planSnapshotDate = isIdealPortfolioTab
    ? idealData?.allocationPlanSnapshotDate
    : listPlansData?.planSnapshotDate
  const riskProfileLabel =
    planMode === PlansPlanMode.REBALANCE ? formatRiskProfileLabel(rebalancePlansData?.riskProfile) : ''
  const viewRebalanceRiskProfileCta = rebalancePlansData?.viewRebalanceRiskProfileCta
  const resetAllocationCta = resetAllocationCtaForPlanMode(
    planMode,
    allocationPlansData?.resetAllocation,
    idealData?.resetAllocation
  )
  const resetStrategyRiskProfile = riskProfileForPlanMode(
    planMode,
    allocationPlansData?.riskProfile,
    idealData?.riskProfile
  )
  const lastRefreshText = convertEpochToDate(planSnapshotDate ?? 0, '%_d %B %Y')
  const analyticsScreen = PlansLumpsumListScreenByPlanMode?.[planMode]

  useWebViewPageVisibility({
    enabled: awaitingResume,
    onResume: () => {
      setAwaitingResume(false)
      markTimerResumeRefetch()
      if (isIdealPortfolioTab) {
        idealPlansQuery.refetch()
        return
      }
      allocationPlansQuery.refetch()
    }
  })

  function handlePlanDetailsClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: analyticsScreen,
        eventId: PlansLumpsumListEventIds.FooterPlanDetails,
        buttonLabel: 'Plan details'
      }
    })
    openBottomSheet(
      <PlanDetailsBottomSheet onDismiss={closeBottomSheet} screenName={analyticsScreen} />
    )
  }

  function handleRiskProfileClick() {
    if (viewRebalanceRiskProfileCta == null) return
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: analyticsScreen,
        eventId: PlansLumpsumListEventIds.ViewRebalanceRiskProfile,
        buttonLabel: riskProfileLabel
      }
    })
    postCTA(JSON.stringify(viewRebalanceRiskProfileCta))
  }

  return (
    <div className="bg-[#F0F4F7] px-5 pt-8 pb-12">
      <div className="flex flex-col gap-2">
        {/* <div
          className="text-[13px] font-[500] text-[#6A6E6E] underline cursor-pointer"
          onClick={handlePlanDetailsClick}
        >
          What does your plan include and exclude?
        </div> */}
        {riskProfileLabel.length > 0 ? (
          <div className="text-[14px] text-[#6A6E6E]">
            Based on your risk profile:{' '}
            <span className="font-[500] underline cursor-pointer" onClick={handleRiskProfileClick}>
              {riskProfileLabel}
            </span>
          </div>
        ) : null}
        {lastRefreshText.length > 0 ? (
          <div className="text-[14px] text-[#6A6E6E]">
            Funds shown as per last refresh on {lastRefreshText}
          </div>
        ) : null}
      </div>

      {resetAllocationCta != null ? (
        <ResetStrategyBanner
          riskProfile={resetStrategyRiskProfile}
          onClick={() => {
            lightImpactVibrate()
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                ScreenName: analyticsScreen,
                eventId: PlansLumpsumListEventIds.ResetStrategy,
                buttonLabel: 'Reset strategy'
              }
            })
            openBottomSheet(
              <ResetStrategyBottomSheet
                screenName={analyticsScreen}
                onDismiss={closeBottomSheet}
                onConfirm={() => {
                  trackEvent({
                    eventName: EventNames.ButtonTap,
                    payload: {
                      ScreenName: analyticsScreen,
                      eventId: PlansLumpsumListEventIds.ResetStrategyConfirm,
                      buttonLabel: 'Confirm reset'
                    }
                  })
                  closeBottomSheet()
                  // Resume refetch only after the native reset screen is opened.
                  setAwaitingResume(true)
                  postCTA(JSON.stringify(resetAllocationCta))
                }}
              />
            )
          }}
        />
      ) : null}

      <PlansFaqsSection
        faqs={faqsForPlanMode(planMode)}
        maxVisibleFaqs={FAQ_PREVIEW_COUNT}
        screenName={analyticsScreen}
        faqAccordionEventId={PlansLumpsumListEventIds.FaqAccordion}
        faqViewAllEventId={PlansLumpsumListEventIds.FaqViewAll}
      />

      <DisclaimerBlock />
    </div>
  )
}

function riskProfileForPlanMode(
  planMode: PlansPlanMode,
  allocationRiskProfile: AllocationRiskProfile | null | undefined,
  idealRiskProfile: AllocationRiskProfile | null | undefined
): AllocationRiskProfile | null | undefined {
  if (planMode === PlansPlanMode.IDEAL_PORTFOLIO) return idealRiskProfile
  if (planMode === PlansPlanMode.ALLOCATION) return allocationRiskProfile
  return undefined
}

function resetAllocationCtaForPlanMode(
  planMode: PlansPlanMode,
  allocationResetCta: unknown,
  idealResetCta: unknown
): unknown {
  if (planMode === PlansPlanMode.IDEAL_PORTFOLIO) return idealResetCta
  if (planMode === PlansPlanMode.ALLOCATION) return allocationResetCta
  return undefined
}

function faqsForPlanMode(planMode: PlansPlanMode): PlansFaq[] {
  if (planMode === PlansPlanMode.REBALANCE) {
    return LUMPSUM_REBALANCE_PLANS_FAQS
  }
  if (planMode === PlansPlanMode.IDEAL_PORTFOLIO) {
    return LUMPSUM_IDEAL_PLANS_FAQS
  }
  return LUMPSUM_PLANS_FAQS
}

function formatRiskProfileLabel(riskProfile: AllocationRiskProfile | null | undefined): string {
  if (!riskProfile) return ''
  return riskProfile
    .toLowerCase()
    .split('_')
    .map((profilePart) => profilePart.charAt(0).toUpperCase() + profilePart.slice(1))
    .join(' ')
}

export function DisclaimerBlock() {
  return (
    <div className="mt-10 flex flex-col gap-6">
      <div className="text-[13px] leading-[150%] text-[#8E9393]">
        All recommendations are thoroughly backtested by our in-house SEBI registered experts.
      </div>
      <div className="text-[13px] leading-[150%] text-[#8E9393]">
        We understand that past success of backtesting doesn&apos;t guarantee future returns, and neither do we.
        Rest assured, we are actively monitoring your portfolio with our proprietary rating engine built by our
        experts, to give the best possible results on your investments.
      </div>
      <div className="text-[13px] leading-[150%] text-[#8E9393]">
        Disclaimer: Information provided is intended solely for the client&apos;s consumption and should not be
        redistributed. Mutual fund investments are subject to market risks.
      </div>
    </div>
  )
}
